<div class="dashboards-page">
  <header class="page-header" *ngIf="viewMode !== 'detail'">
    <div>
      <h1>Dashboards</h1>
      <p class="subtitle" *ngIf="canManageDashboards; else readOnlyDashboardSubtitle">
        Build saved dashboard views from scheduled report previews, render KPI tiles, and add buttons that jump into
        the existing report workspace.
      </p>
      <ng-template #readOnlyDashboardSubtitle>
        <p class="subtitle">View dashboards that have been shared with you.</p>
      </ng-template>
    </div>

    <div class="header-actions">
      <button mat-stroked-button type="button" *ngIf="viewMode !== 'list'" (click)="returnToList()">Back to Dashboards</button>
      <button mat-flat-button color="primary" type="button" *ngIf="viewMode === 'list' && canManageDashboards" (click)="startCreate()">
        Create Dashboard
      </button>
    </div>
  </header>

  <section class="page-loading" *ngIf="isLoading || isBootstrapping">
    <mat-spinner [diameter]="32"></mat-spinner>
    <span>Loading dashboards</span>
  </section>

  <ng-container *ngIf="!isLoading && !isBootstrapping">
    <section class="list-shell" *ngIf="viewMode === 'list'">
      <div class="toolbar">
        <mat-form-field appearance="outline">
          <mat-label>Search dashboards</mat-label>
          <input
            matInput
            [(ngModel)]="searchValue"
            [placeholder]="canManageDashboards ? 'Name, description, tiles, actions' : 'Name or description'"
          />
        </mat-form-field>

        <mat-form-field appearance="outline" *ngIf="canManageDashboards">
          <mat-label>Status</mat-label>
          <mat-select [(ngModel)]="statusFilter">
            <mat-option value="all">All statuses</mat-option>
            <mat-option value="draft">Draft</mat-option>
            <mat-option value="active">Active</mat-option>
            <mat-option value="archived">Archived</mat-option>
          </mat-select>
        </mat-form-field>

        <button mat-stroked-button type="button" (click)="refreshList()">Refresh</button>
      </div>

      <ng-container *ngIf="filteredDashboards.length; else emptyState">
        <div class="dashboard-grid">
          <article class="dashboard-card" *ngFor="let dashboard of filteredDashboards; trackBy: trackByDashboardId">
            <div class="dashboard-card-head">
              <div>
                <h2>{{ dashboard.name }}</h2>
                <p *ngIf="dashboard.description">{{ dashboard.description }}</p>
              </div>
              <span
                *ngIf="canManageDashboards"
                class="status-pill"
                [class.active]="dashboard.status === 'active'"
                [class.archived]="dashboard.status === 'archived'"
              >
                {{ dashboard.status }}
              </span>
            </div>

            <div class="dashboard-card-meta">
              <div>
                <span class="meta-label">Tiles</span>
                <strong>{{ dashboard.tiles.length }}</strong>
              </div>
              <div *ngIf="canManageDashboards">
                <span class="meta-label">Buttons</span>
                <strong>{{ dashboard.actions.length }}</strong>
              </div>
              <div>
                <span class="meta-label">Updated</span>
                <strong>{{ dashboard.updatedAt | date:'short' }}</strong>
              </div>
              <div *ngIf="canManageDashboards">
                <span class="meta-label">Permissions</span>
                <strong>{{ dashboard.permissionCount || 0 }}</strong>
              </div>
            </div>

            <div class="dashboard-card-actions">
              <button mat-stroked-button type="button" (click)="openDashboard(dashboard)">
                {{ canManageDashboards ? 'Open' : 'View' }}
              </button>
              <button mat-stroked-button type="button" *ngIf="canManageDashboards" (click)="editDashboard(dashboard)">Edit</button>
              <button mat-button color="warn" type="button" *ngIf="canManageDashboards" (click)="confirmDeleteDashboard(dashboard, $event)">Delete</button>
            </div>
          </article>
        </div>
      </ng-container>

      <ng-template #emptyState>
        <section class="empty-state">
          <mat-icon>dashboard_customize</mat-icon>
          <h2>{{ canManageDashboards ? 'No dashboards yet' : 'No dashboards available' }}</h2>
          <p>
            {{
              canManageDashboards
                ? 'Create a dashboard to start surfacing rolled-up report metrics and quick links.'
                : 'No dashboards have been shared with you yet.'
            }}
          </p>
        </section>
      </ng-template>
    </section>

    <section class="editor-shell" *ngIf="viewMode === 'create' || viewMode === 'edit'">
      <form [formGroup]="dashboardForm" class="editor-grid">
        <article class="panel">
          <div class="panel-header">
            <span class="panel-step">1</span>
            <div>
              <h2>{{ viewMode === 'create' ? 'Create dashboard' : 'Edit dashboard' }}</h2>
              <p>Name the dashboard and choose whether it is still a draft or ready for use.</p>
            </div>
          </div>

          <div class="form-grid two-up">
            <mat-form-field appearance="outline">
              <mat-label>Dashboard name</mat-label>
              <input matInput formControlName="name" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Status</mat-label>
              <mat-select formControlName="status">
                <mat-option value="draft">Draft</mat-option>
                <mat-option value="active">Active</mat-option>
                <mat-option value="archived">Archived</mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Description</mat-label>
              <textarea matInput rows="3" formControlName="description"></textarea>
            </mat-form-field>
          </div>
        </article>

        <article class="panel">
          <div class="panel-header">
            <span class="panel-step">2</span>
            <div>
              <h2>Tiles</h2>
              <p>Each tile reads from a saved scheduled report preview and renders one summary metric.</p>
            </div>
          </div>

          <div class="empty-inline-state" *ngIf="!tilesArray.length">
            Add at least one tile to surface a rolled-up metric.
          </div>

          <div class="selected-card" *ngFor="let tile of tilesArray.controls; let i = index; trackBy: trackByControlId" [formGroup]="asFormGroup(tile)">
            <div class="selected-card-head">
              <div class="selected-card-copy">
                <strong>{{ tile.get('title')?.value || ('Tile ' + (i + 1)) }}</strong>
                <p>{{ getReportName(tile.get('reportId')?.value) }}</p>
              </div>
              <button mat-icon-button type="button" (click)="removeTile(i)" aria-label="Remove Tile">
                <mat-icon>close</mat-icon>
              </button>
            </div>

            <div class="form-grid three-up compact">
              <mat-form-field appearance="outline">
                <mat-label>Tile title</mat-label>
                <input matInput formControlName="title" />
                <mat-error *ngIf="tile.get('title')?.hasError('required') && tile.get('title')?.touched">
                  Tile title is required.
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Saved report</mat-label>
                <mat-select formControlName="reportId" (selectionChange)="onTileReportChange(tile)">
                  <mat-option *ngFor="let report of reports; trackBy: trackByReportId" [value]="report.id">
                    {{ report.name }}
                  </mat-option>
                </mat-select>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Metric</mat-label>
                <mat-select formControlName="metricType" (selectionChange)="onTileMetricTypeChange(tile)">
                  <mat-option *ngFor="let metric of metricTypeOptions" [value]="metric.value">{{ metric.label }}</mat-option>
                </mat-select>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Description</mat-label>
                <input matInput formControlName="description" />
              </mat-form-field>

              <mat-form-field appearance="outline" *ngIf="tile.get('metricType')?.value !== 'count'">
                <mat-label>Preview column</mat-label>
                <mat-select *ngIf="getTileFieldOptions(tile).length" formControlName="fieldKey">
                  <mat-option *ngFor="let option of getTileFieldOptions(tile)" [value]="option.value">{{ option.label }}</mat-option>
                </mat-select>
                <input
                  *ngIf="!getTileFieldOptions(tile).length"
                  matInput
                  formControlName="fieldKey"
                  placeholder="Preview column header or field"
                />
                <mat-hint>{{ isTileFieldOptionsLoading(tile) ? 'Loading preview columns...' : getTileFieldHint(tile) }}</mat-hint>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Display format</mat-label>
                <mat-select formControlName="valueFormat">
                  <mat-option *ngFor="let format of valueFormatOptions" [value]="format.value">{{ format.label }}</mat-option>
                </mat-select>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Precision</mat-label>
                <input matInput type="number" min="0" max="4" formControlName="precision" />
              </mat-form-field>
            </div>
          </div>

          <div class="builder-footer">
            <button mat-stroked-button type="button" (click)="addTile()">Add Tile</button>
          </div>
        </article>

        <article class="panel">
          <div class="panel-header">
            <span class="panel-step">3</span>
            <div>
              <h2>Report buttons</h2>
              <p>Create buttons that open an existing saved report or start a new report from a template.</p>
            </div>
          </div>

          <div class="empty-inline-state" *ngIf="!actionsArray.length">
            Add action buttons when the dashboard should jump directly into the report workspace.
          </div>

          <div class="selected-card" *ngFor="let action of actionsArray.controls; let i = index; trackBy: trackByControlId" [formGroup]="asFormGroup(action)">
            <div class="selected-card-head">
              <div class="selected-card-copy">
                <strong>{{ action.get('label')?.value || ('Action ' + (i + 1)) }}</strong>
                <p>{{ action.get('targetType')?.value === 'saved_report' ? getReportName(action.get('reportId')?.value) : getTemplateLabel(action.get('templateKey')?.value) }}</p>
              </div>
              <button mat-icon-button type="button" (click)="removeAction(i)" aria-label="Remove Report Button">
                <mat-icon>close</mat-icon>
              </button>
            </div>

            <div class="form-grid three-up compact">
              <mat-form-field appearance="outline">
                <mat-label>Button label</mat-label>
                <input matInput formControlName="label" />
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Button type</mat-label>
                <mat-select formControlName="targetType">
                  <mat-option value="saved_report">Open saved report</mat-option>
                  <mat-option value="report_template">Start template report</mat-option>
                </mat-select>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Description</mat-label>
                <input matInput formControlName="description" />
              </mat-form-field>

              <mat-form-field appearance="outline" *ngIf="action.get('targetType')?.value === 'saved_report'">
                <mat-label>Saved report</mat-label>
                <mat-select formControlName="reportId">
                  <mat-option *ngFor="let report of reports" [value]="report.id">{{ report.name }}</mat-option>
                </mat-select>
              </mat-form-field>

              <mat-form-field appearance="outline" *ngIf="action.get('targetType')?.value === 'report_template'">
                <mat-label>Report template</mat-label>
                <mat-select formControlName="templateKey">
                  <mat-option *ngFor="let template of templates" [value]="template.key">{{ template.label }}</mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>

          <div class="builder-footer">
            <button mat-stroked-button type="button" (click)="addAction()">Add Report Button</button>
          </div>
        </article>

        <article class="panel">
          <div class="panel-header">
            <span class="panel-step">4</span>
            <div>
              <h2>Dashboard access</h2>
              <p>Grant this dashboard to specific users, companies, teams, or brands.</p>
            </div>
          </div>

          <div class="inline-loading compact-loading" *ngIf="isLoadingPermissions">
            <mat-spinner [diameter]="24"></mat-spinner>
            <span>Loading dashboard permissions</span>
          </div>

          <div class="empty-inline-state" *ngIf="!isLoadingPermissions && !permissionsArray.length">
            No access entries have been added. Superadmins can still manage this dashboard.
          </div>

          <div
            class="selected-card permission-card"
            *ngFor="let permission of permissionsArray.controls; let i = index; trackBy: trackByPermissionControl"
            [formGroup]="asFormGroup(permission)"
          >
            <div class="selected-card-head">
              <div class="selected-card-copy">
                <strong>{{ getPermissionSubjectTypeLabel(permission.get('subjectType')?.value) }}</strong>
                <p>{{ getPermissionSubjectSelectionLabel(permission) }}</p>
              </div>
              <button mat-icon-button type="button" (click)="removePermission(i)" aria-label="Remove Dashboard Access">
                <mat-icon>close</mat-icon>
              </button>
            </div>

            <div class="form-grid two-up compact">
              <mat-form-field appearance="outline">
                <mat-label>Access type</mat-label>
                <mat-select formControlName="subjectType" (selectionChange)="onPermissionSubjectTypeChange(permission)">
                  <mat-option *ngFor="let option of permissionSubjectTypeOptions" [value]="option.value">
                    {{ option.label }}
                  </mat-option>
                </mat-select>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>{{ getPermissionSubjectTypeLabel(permission.get('subjectType')?.value) }}</mat-label>
                <mat-select
                  formControlName="subjectId"
                  panelClass="member-addedit-select-search-panel"
                  (openedChange)="onPermissionSubjectSelectOpened(permission, $event)"
                >
                  <mat-select-trigger>
                    {{ getPermissionSubjectSelectionLabel(permission) }}
                  </mat-select-trigger>
                  <mat-option
                    *ngIf="shouldShowSelectSearch(getPermissionSubjectOptions(permission.get('subjectType')?.value).length)"
                    class="contains-mat-select-search"
                    [disabled]="true"
                  >
                    <ngx-mat-select-search
                      [formControl]="getPermissionSearchControl(permission.get('subjectType')?.value)"
                      noEntriesFoundLabel="No matches"
                      placeholderLabel="Search..."
                    ></ngx-mat-select-search>
                  </mat-option>
                  <mat-option *ngIf="isPermissionOptionsLoading(permission.get('subjectType')?.value)" [disabled]="true">
                    Loading {{ getPermissionSubjectLoadingLabel(permission.get('subjectType')?.value) }}...
                  </mat-option>
                  <mat-option
                    *ngFor="let option of getFilteredPermissionSubjectOptions(permission.get('subjectType')?.value); trackBy: trackByPermissionOption"
                    [value]="option.id"
                  >
                    {{ option.label }}<span *ngIf="option.secondaryLabel"> - {{ option.secondaryLabel }}</span>
                  </mat-option>
                </mat-select>
                <mat-error *ngIf="permission.get('subjectId')?.hasError('required') && permission.get('subjectId')?.touched">
                  {{ getPermissionSubjectTypeLabel(permission.get('subjectType')?.value) }} is required.
                </mat-error>
              </mat-form-field>
            </div>
          </div>

          <div class="builder-footer">
            <button mat-stroked-button type="button" (click)="addPermission()" [disabled]="isLoadingPermissions">Add Access</button>
          </div>
        </article>

        <article class="panel preview-panel">
          <div class="panel-header">
            <span class="panel-step">5</span>
            <div>
              <h2>Preview</h2>
              <p>Render the current dashboard definition against the selected scheduled reports.</p>
            </div>
          </div>

          <div class="validation-list" *ngIf="validationMessages.length">
            <h3>Fix these items first</h3>
            <ul>
              <li *ngFor="let message of validationMessages">{{ message }}</li>
            </ul>
          </div>

          <div class="inline-loading" *ngIf="isRendering">
            <mat-spinner [diameter]="28"></mat-spinner>
            <span>Rendering dashboard preview</span>
          </div>

          <div class="empty-inline-state" *ngIf="!isRendering && !editorRenderState">
            Preview the dashboard to validate tile values before saving it.
          </div>

          <ng-container *ngIf="editorRenderState">
            <div class="warning-list" *ngIf="editorRenderState.warnings.length">
              <h3>Preview warnings</h3>
              <ul>
                <li *ngFor="let warning of editorRenderState.warnings">{{ warning }}</li>
              </ul>
            </div>

            <div class="tile-grid">
              <article class="tile-card" *ngFor="let tile of editorRenderState.tiles; trackBy: trackByRenderedTile">
                <div class="tile-card-head">
                  <div>
                    <h3>{{ tile.title }}</h3>
                    <p class="report-description" *ngIf="tile.reportDescription">{{ tile.reportDescription }}</p>
                  </div>
                  <div class="tile-card-actions">
                    <button mat-button type="button" *ngIf="tile.status === 'success'" (click)="toggleTilePreview(tile)">
                      {{ isTilePreviewOpen(tile) ? 'Hide Preview' : 'Preview Results' }}
                    </button>
                    <button mat-button type="button" *ngIf="tile.status === 'success'" (click)="sendTileReport(tile)">Send Report</button>
                    <button mat-button type="button" *ngIf="tile.reportId" (click)="openTileReport(tile)">Open Report</button>
                  </div>
                </div>

                <strong class="tile-value" [class.error]="tile.status === 'error'">{{ tile.displayValue }}</strong>
                <p class="tile-helper" *ngIf="tile.status === 'success'">{{ tile.helperText }}</p>
                <p class="tile-helper error-text" *ngIf="tile.status === 'error'">{{ tile.errorMessage }}</p>

                <div class="tile-preview" *ngIf="tile.status === 'success' && isTilePreviewOpen(tile)">
                  <p class="tile-preview-note">{{ getTilePreviewSummary(tile) }}</p>
                  <div class="empty-inline-state compact-empty" *ngIf="!tile.previewRows.length">
                    No preview rows returned for this report.
                  </div>
                  <div class="table-shell" *ngIf="tile.previewRows.length">
                    <table class="preview-table">
                      <thead>
                        <tr>
                          <th *ngFor="let header of getTilePreviewHeaders(tile)">
                            <button type="button" class="preview-sort-button" (click)="toggleTilePreviewSort(tile, header)">
                              <span>{{ header }}</span>
                              <span class="preview-sort-indicator" *ngIf="getTilePreviewSortDirection(tile, header) === 'asc'">arrow_upward</span>
                              <span class="preview-sort-indicator" *ngIf="getTilePreviewSortDirection(tile, header) === 'desc'">arrow_downward</span>
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr *ngFor="let row of getSortedTilePreviewRows(tile)">
                          <td *ngFor="let header of getTilePreviewHeaders(tile)">
                            {{ getTilePreviewCellText(tile, getTilePreviewCellValue(tile, row, header)) }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </article>
            </div>
          </ng-container>
        </article>
      </form>

      <div class="editor-actions">
        <button mat-stroked-button type="button" (click)="previewDashboard()">Preview Dashboard</button>
        <button mat-flat-button color="primary" type="button" (click)="saveDashboard()" [disabled]="isSaving">
          {{ isSaving ? 'Saving...' : 'Save Dashboard' }}
        </button>
      </div>
    </section>

    <section class="detail-shell" *ngIf="viewMode === 'detail' && detailDashboard">
      <div class="detail-topline">
        <div>
          <h2>{{ detailDashboard.name }}</h2>
          <p class="subtitle" *ngIf="detailDashboard.description">{{ detailDashboard.description }}</p>
        </div>

        <div class="header-actions">
          <span
            *ngIf="canManageDashboards"
            class="status-pill"
            [class.active]="detailDashboard.status === 'active'"
            [class.archived]="detailDashboard.status === 'archived'"
          >
            {{ detailDashboard.status }}
          </span>
          <button mat-stroked-button type="button" (click)="refreshDetail()">Refresh</button>
          <button mat-stroked-button type="button" (click)="returnToList()">Back to Dashboards</button>
          <button mat-stroked-button type="button" *ngIf="canManageDashboards" (click)="editDashboard(detailDashboard)">Edit</button>
          <button mat-button color="warn" type="button" *ngIf="canManageDashboards" (click)="confirmDeleteDashboard(detailDashboard)">Delete</button>
        </div>
      </div>

      <div class="dashboard-actions-bar" *ngIf="canManageDashboards && detailDashboard.actions.length">
        <button mat-raised-button color="accent" type="button" *ngFor="let action of detailDashboard.actions" (click)="openAction(action)">
          {{ action.label }}
        </button>
      </div>

      <div class="inline-loading" *ngIf="isRendering && !detailRenderState">
        <mat-spinner [diameter]="28"></mat-spinner>
        <span>Rendering dashboard</span>
      </div>

      <div class="warning-list" *ngIf="detailRenderState?.warnings?.length">
        <h3>Render warnings</h3>
        <ul>
          <li *ngFor="let warning of detailRenderState?.warnings">{{ warning }}</li>
        </ul>
      </div>

      <div class="empty-inline-state" *ngIf="detailRenderState && !detailRenderState.tiles.length">
        {{ canManageDashboards ? 'This dashboard has no tiles yet. Edit it to add metrics.' : 'This dashboard has no tiles available.' }}
      </div>

      <div class="tile-grid" *ngIf="detailRenderState?.tiles?.length">
        <article class="tile-card" *ngFor="let tile of detailRenderState?.tiles; trackBy: trackByRenderedTile">
          <div class="tile-card-head">
            <div>
              <h3>{{ tile.title }}</h3>
              <p class="report-description" *ngIf="tile.reportDescription">{{ tile.reportDescription }}</p>
            </div>
            <div class="tile-card-actions">
              <button mat-button type="button" *ngIf="tile.status === 'success'" (click)="toggleTilePreview(tile)">
                {{ isTilePreviewOpen(tile) ? 'Hide Preview' : 'Preview Results' }}
              </button>
              <button mat-button type="button" *ngIf="canManageDashboards && tile.status === 'success'" (click)="sendTileReport(tile)">Send Report</button>
              <button mat-button type="button" *ngIf="canManageDashboards && tile.reportId" (click)="openTileReport(tile)">Open Report</button>
            </div>
          </div>

          <strong class="tile-value" [class.error]="tile.status === 'error'">{{ tile.displayValue }}</strong>
          <p class="tile-helper" *ngIf="tile.status === 'success'">{{ tile.helperText }}</p>
          <p class="tile-helper error-text" *ngIf="tile.status === 'error'">{{ tile.errorMessage }}</p>

          <ul class="tile-warning-list" *ngIf="tile.warnings.length">
            <li *ngFor="let warning of tile.warnings">{{ warning }}</li>
          </ul>

          <div class="tile-preview" *ngIf="tile.status === 'success' && isTilePreviewOpen(tile)">
            <div class="tile-preview-toolbar">
              <p class="tile-preview-note">{{ getTilePreviewSummary(tile) }}</p>
              <button
                mat-stroked-button
                type="button"
                *ngIf="tile.reportId"
                [disabled]="isTileReportDownloading(tile)"
                (click)="downloadTileReport(tile)"
              >
                {{ isTileReportDownloading(tile) ? 'Downloading...' : 'Download Report' }}
              </button>
            </div>
            <div class="empty-inline-state compact-empty" *ngIf="!tile.previewRows.length">
              No preview rows returned for this report.
            </div>
            <div class="table-shell" *ngIf="tile.previewRows.length">
              <table class="preview-table">
                <thead>
                  <tr>
                    <th *ngFor="let header of getTilePreviewHeaders(tile)">
                      <button type="button" class="preview-sort-button" (click)="toggleTilePreviewSort(tile, header)">
                        <span>{{ header }}</span>
                        <span class="preview-sort-indicator" *ngIf="getTilePreviewSortDirection(tile, header) === 'asc'">arrow_upward</span>
                        <span class="preview-sort-indicator" *ngIf="getTilePreviewSortDirection(tile, header) === 'desc'">arrow_downward</span>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of getSortedTilePreviewRows(tile)">
                    <td *ngFor="let header of getTilePreviewHeaders(tile)">
                      {{ getTilePreviewCellText(tile, getTilePreviewCellValue(tile, row, header)) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </article>
      </div>

      <div class="detail-meta" *ngIf="detailRenderState">
        <span><strong>Rendered:</strong> {{ detailRenderState.renderedAt | date:'medium' }}</span>
        <span><strong>Tiles:</strong> {{ detailRenderState.tiles.length }}</span>
      </div>
    </section>
  </ng-container>
</div>
